import * as _ from "lodash";

import { ExtensionBase } from "../extensionBase";
import { UiUtils } from "../../common/uiUtils";
import { SwecoModelStructurePanel } from "./swecoModelStructurePanel";
import { addDndListener, viewerOptions } from "../../viewer";
import { ModelApi } from "../../api/modelApi";
import * as SmartDrawings from "../../smartDrawings";

export type MappingItem = {
    guid?: string,
    name?: string,
    parent?: string,
    virtual?: boolean
};

export type Mapping = {
    name: string,
    items: MappingItem[]
}

/**
 * Extension that shows a custom model hierarchy if it can be constructed for the model.
 */
export class ModelStructureExtension extends ExtensionBase {

    static extensionName = "ModelStructureExtension";

    private structureButton: Autodesk.Viewing.UI.Button;
    private subToolbar: Autodesk.Viewing.UI.ControlGroup;
    private panel: SwecoModelStructurePanel;

    private mapping: Mapping;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: any) {
        super(viewer, options);
        this.viewer.addEventListener(SmartDrawings.SdEvents.ID_MAP_LOADED, this.onIdMapLoaded);
    }

    static registerExtension() {
        super.registerExtension(ModelStructureExtension.extensionName, ModelStructureExtension);
    }

    load(): boolean {
        console.log("ModelStructureExtension is loaded!");
      
        this.panel = new SwecoModelStructurePanel(this.viewer);
        this.attachDnd();
        return super.load();
    };

    unload(): boolean {
        this.viewer.getToolbar(false).removeControl(this.subToolbar);
        return super.unload();
    };

    private attachDnd() {
        addDndListener((file, result) => {
            if (file.name.indexOf("modelTree") >= 0) {
                this.validateMapping(result);
                return true;
            }
            return false;
        });
    }

    private validateMapping(mapping: Mapping) {
        if (mapping.items != null && mapping.items.length > 0) {
            this.mapping = mapping;
            if (this.panel.isVisible()) {
                const tree = this.buildTree(this.mapping, this.viewer.model.getInstanceTree()).tree;
                this.panel.setDataVisible(true, tree);
            }
            console.log("Mapping import successful.");
        } else {
            this.mapping = null;
            console.log("Mapping import failed");
        }
    }

    onToolbarCreated(): void {
        this.structureButton = UiUtils.createButton({
            id: "SmartDrawings.ModelStructureButton",
            tooltip: "Model structure",
            iconClass: "ion-md-git-merge",
            onClick: async () => {
                if (!this.panel.isVisible()) {
                    if (this.mapping != null) {
                        const tree = this.buildTree(this.mapping, this.viewer.model.getInstanceTree()).tree;
                        this.panel.setDataVisible(true, tree);
                    } else {
                        this.panel.setPendingVisible();
                        try {
                            const { data } = await ModelApi.getModelTree(viewerOptions.guid);
                            this.mapping = data;
                            const tree = this.buildTree(this.mapping, this.viewer.model.getInstanceTree()).tree;
                            this.panel.setDataVisible(true, tree);
                        } catch (error) {
                            this.panel.setNoStructureVisible();
                            console.error(error);
                        }                
                    }
                } else {
                    this.panel.setDataVisible(false);
                }
            }
        });

        this.structureButton.setState(Autodesk.Viewing.UI.Button.State.DISABLED);

        this.panel.addVisibilityListener(visible => {
            this.structureButton.setState(visible ? Autodesk.Viewing.UI.Button.State.ACTIVE : Autodesk.Viewing.UI.Button.State.INACTIVE);
        });

        this.subToolbar = this.getControlGroup("SmartDrawings.Extensions");
        this.subToolbar.addControl(this.structureButton);
    }

    private onIdMapLoaded = () => {
        this.structureButton.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
    } 

    private buildTree(mapping: Mapping, instanceTree) {
        const treeData = { nodeMap: {}, tree: [] };
        if (mapping == null) {
            return treeData;
        }

        for (let item of mapping.items) {
            if (item.parent == null) {
                if (item.virtual) {
                    const node = { id: item.guid, title: item.name, children: [] };
                    treeData.tree.push(node);
                    treeData.nodeMap[item.guid] = node;
                } else {
                    treeData.tree.push(this.addMappedNode(item, instanceTree, treeData, mapping));
                }
            } else {
                const parent = treeData.nodeMap[item.parent];
                if (item.virtual) {
                    const node = { id: item.guid, title: item.name, children: [] };
                    parent.children.push(node);
                    treeData.nodeMap[item.guid] = node;
                } else {                
                    parent.children.push(this.addMappedNode(item, instanceTree, treeData, mapping));
                }
            }
        }

        const rootWithUnhandledNodes = this.addNode(instanceTree.getRootId(), instanceTree, treeData, mapping);
        if (rootWithUnhandledNodes != null) {
            rootWithUnhandledNodes.title = "Other nodes"; 
            treeData.tree.push(rootWithUnhandledNodes);
        }

        return treeData;
    }

    // Add a node from mapping.
    private addMappedNode(mappingItem: MappingItem, instanceTree, treeData, mapping: Mapping) {
        // Combo model not supported
        const dbId = viewerOptions.idMap.nodeId(viewerOptions.guid, mappingItem.guid);
 
        const children = [];
        if (instanceTree.getChildCount(dbId) > 0) {
            instanceTree.enumNodeChildren(dbId,
                childId => {
                    const child = this.addNode(childId, instanceTree, treeData, mapping);
                    if (child != null) {
                        children.push(child);
                    }
                },
                false);
        }

        const node = { id: dbId || mappingItem.guid, title: mappingItem.name || instanceTree.getNodeName(dbId, true), children: children };
        treeData.nodeMap[mappingItem.guid] = node;
        treeData.nodeMap[dbId] = node;
        return node;
    }

    // Add a node not directly given in mapping.
    private addNode(dbId, instanceTree, treeData, mapping: Mapping) {

        // Node is mapped to somewhere else, don't handle here. Or if it's already processed.
        // Combo model not supported.
        if (_.some(mapping.items, m => viewerOptions.idMap.nodeId(viewerOptions.guid, m.guid) === dbId) || treeData.nodeMap[dbId]) {
            return null;
        }

        const children = [];
        if (instanceTree.getChildCount(dbId) > 0) {
            instanceTree.enumNodeChildren(dbId,
                childId => {
                    const child = this.addNode(childId, instanceTree, treeData, mapping);
                    if (child != null) {
                        children.push(child);
                    }
                },
                false);
        }

        const node = { id: dbId, title: instanceTree.getNodeName(dbId, true), children: children };
        treeData.nodeMap[dbId] = node;
        return node;
    }
}